import { parseEpisodeTotal, parseScore, stripHtml } from '@/lib/format';
import { resolveImageUrl } from '@/lib/image';
import { ANIME_CATEGORY_SLUG, ANIME_MOVIE_TYPE } from '@/lib/constants';
import type {
	ApiEpisodeServer,
	ApiMovieDetail,
	ApiMovieListItem,
	ApiPagination,
	ApiTaxonomy,
} from '@/types/api';
import type {
	AnimeDetail,
	AnimeServer,
	AnimeSummary,
	LibraryEntry,
	Pagination,
	Taxonomy,
} from '@/types/anime';

export function mapTaxonomy(items: ApiTaxonomy[] | undefined): Taxonomy[] {
	return (items ?? [])
		.filter((item) => item?.slug && item?.name)
		.map((item) => ({ name: item.name, slug: item.slug }));
}

/**
 * Anime-only = `type === "hoathinh"`.
 * Không dùng thể loại "hoat-hinh" khi đã có type: live-action vẫn có thể bị
 * gắn tag đó, còn nhiều anime lại không gắn.
 * Chỉ fallback sang thể loại khi bản ghi cũ thiếu hẳn field `type`.
 */
export function isAnimeItem(
	item: Pick<ApiMovieListItem, 'type' | 'category'>,
): boolean {
	if (item.type) return item.type === ANIME_MOVIE_TYPE;
	return (item.category ?? []).some(
		(category) => category.slug === ANIME_CATEGORY_SLUG,
	);
}

export function mapSummary(
	item: ApiMovieListItem,
	cdnBase?: string,
): AnimeSummary {
	return {
		id: item._id,
		slug: item.slug,
		title: item.name?.trim() || 'Phim chưa có tên',
		originalTitle: item.origin_name?.trim() ?? '',
		posterUrl: resolveImageUrl(item.poster_url || item.thumb_url, cdnBase),
		thumbUrl: resolveImageUrl(item.thumb_url || item.poster_url, cdnBase),
		year: typeof item.year === 'number' && item.year > 0 ? item.year : null,
		quality: item.quality?.trim() ?? '',
		lang: item.lang?.trim() ?? '',
		episodeCurrent: item.episode_current?.trim() ?? '',
		// Chỉ endpoint chi tiết có episode_total; list thì không.
		totalEpisodes: parseEpisodeTotal(
			(item as Partial<ApiMovieDetail>).episode_total,
		),
		durationLabel: item.time?.trim() ?? '',
		score: parseScore(item.tmdb?.vote_average, item.imdb?.vote_average),
		genres: mapTaxonomy(item.category),
		countries: mapTaxonomy(item.country),
		updatedAt: item.modified?.time ?? null,
	};
}

function mapServers(servers: ApiEpisodeServer[] | undefined): AnimeServer[] {
	return (servers ?? [])
		.map((server, serverIndex) => ({
			name: server.server_name?.trim() || `Server ${serverIndex + 1}`,
			episodes: (server.server_data ?? [])
				.map((episode, episodeIndex) => {
					const name = episode.name?.trim() || `Tập ${episodeIndex + 1}`;
					return {
						name,
						slug: episode.slug?.trim() || `tap-${episodeIndex + 1}`,
						m3u8: episode.link_m3u8?.trim() || null,
						embed: episode.link_embed?.trim() || null,
					};
				})
				// Tập không có cả m3u8 lẫn embed thì không phát được — bỏ hẳn
				// thay vì để người dùng bấm vào rồi thấy player trắng.
				.filter((episode) => episode.m3u8 || episode.embed),
		}))
		.filter((server) => server.episodes.length > 0);
}

export function mapDetail(
	movie: ApiMovieDetail,
	servers: ApiEpisodeServer[] | undefined,
): AnimeDetail {
	return {
		...mapSummary(movie),
		synopsis: stripHtml(movie.content),
		releaseStatus: movie.status?.trim() ?? '',
		views: typeof movie.view === 'number' ? movie.view : 0,
		actors: movie.actor ?? [],
		directors: movie.director ?? [],
		trailerUrl: movie.trailer_url?.trim() || null,
		servers: mapServers(servers),
	};
}

export function mapPagination(
	pagination: ApiPagination | undefined,
): Pagination {
	const totalItems = pagination?.totalItems ?? 0;
	const perPage = pagination?.totalItemsPerPage ?? 0;
	const currentPage = pagination?.currentPage ?? 1;
	const computedPages = perPage > 0 ? Math.ceil(totalItems / perPage) : 0;
	const totalPages = pagination?.totalPages ?? computedPages;

	return {
		totalItems,
		perPage,
		currentPage,
		totalPages: totalPages || (totalItems > 0 ? 1 : 0),
	};
}

/** Rút gọn thành bản ghi nhỏ để lưu localStorage. */
export function toLibraryEntry(
	anime: AnimeSummary,
	extra?: Partial<LibraryEntry>,
): LibraryEntry {
	return {
		slug: anime.slug,
		title: anime.title,
		posterUrl: anime.posterUrl,
		year: anime.year,
		episodeCurrent: anime.episodeCurrent,
		savedAt: Date.now(),
		...extra,
	};
}

/** Dựng lại AnimeSummary tối giản từ localStorage để tái dùng AnimeCard. */
export function fromLibraryEntry(entry: LibraryEntry): AnimeSummary {
	return {
		id: entry.slug,
		slug: entry.slug,
		title: entry.title,
		originalTitle: '',
		posterUrl: entry.posterUrl,
		thumbUrl: entry.posterUrl,
		year: entry.year,
		quality: '',
		lang: '',
		episodeCurrent: entry.episodeCurrent,
		totalEpisodes: null,
		durationLabel: '',
		score: null,
		genres: [],
		countries: [],
		updatedAt: null,
	};
}
